/**
 * 认证错误处理工具函数
 * 统一处理认证相关的错误响应
 */

import { toast } from "sonner";
import type { TFunction } from "i18next";

/**
 * 认证错误类型
 */
export interface AuthError {
  code?: string;
  message?: string;
  details?: Record<string, any>;
}

/**
 * API 响应格式
 */
export interface AuthApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: AuthError;
}

/**
 * 错误代码到 i18n key 的映射
 */
const ERROR_CODE_MAP: Record<string, string> = {
  INVALID_CREDENTIALS: "common.phoneOrPasswordError",
  INVALID_EMAIL: "common.invalidEmail",
  INVALID_PHONE: "common.invalidPhone",
  INVALID_PASSWORD: "common.invalidPassword",
  INVALID_CODE: "common.invalidCode",
  USER_NOT_FOUND: "common.userNotFound",
  USER_EXISTS: "common.userExists",
  EMAIL_NOT_VERIFIED: "common.verifyEmailFirst",
  RATE_LIMITED: "common.rateLimited",
  NETWORK_ERROR: "common.networkError",
  SERVER_ERROR: "common.serverError",
  CAPTCHA_REQUIRED: "common.completeCaptcha",
  CAPTCHA_FAILED: "common.captchaFailed",
};

/**
 * 处理认证 API 错误
 * @param error 错误对象或响应
 * @param t i18n 翻译函数
 * @param fallbackKey 默认错误消息的 i18n key
 */
export function handleAuthError(
  error: AuthError | Error | unknown,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: string) => any,
  fallbackKey = "common.loginFailed",
): string {
  let message: string;

  if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === "object" && error !== null) {
    const authError = error as AuthError;

    // 优先使用错误代码映射
    if (authError.code && ERROR_CODE_MAP[authError.code]) {
      message = t(ERROR_CODE_MAP[authError.code]);
    } else if (authError.message) {
      message = authError.message;
    } else {
      message = t(fallbackKey);
    }
  } else {
    message = t(fallbackKey);
  }

  toast.error(message);
  return message;
}

/**
 * 处理认证 API 响应
 * @param response fetch 响应对象
 * @param t i18n 翻译函数
 * @returns 解析后的响应数据
 * @throws 如果响应不成功，抛出错误
 */
export async function handleAuthResponse<T>(
  response: Response,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: (key: string) => any,
): Promise<AuthApiResponse<T>> {
  const data: AuthApiResponse<T> = await response.json();

  if (!response.ok || !data.success) {
    const errorMessage = handleAuthError(data.error, t);
    throw new Error(errorMessage);
  }

  return data;
}

/**
 * 创建认证 API 请求
 * @param url 请求 URL
 * @param options 请求选项
 * @returns fetch 响应
 */
export async function authFetch(
  url: string,
  options: RequestInit = {},
): Promise<Response> {
  const csrfToken = document.querySelector<HTMLMetaElement>(
    'meta[name="csrf-token"]',
  )?.content;

  const headers: HeadersInit = {
    "Content-Type": "application/json",
    Accept: "application/json",
    ...options.headers,
  };

  if (csrfToken) {
    (headers as Record<string, string>)["X-CSRF-Token"] = csrfToken;
  }

  return fetch(url, {
    ...options,
    headers,
    credentials: "same-origin",
  });
}
